import {
  useCallback,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from 'react';

import { isExpressionTriggerKey } from './expression-trigger-helpers.js';
import { buildTimeframeClipboardText } from './selection-copy-helpers.js';
import {
  extendSelection,
  extendSelectionToAll,
  removeContentEditable,
  restoreSpinButtonSnapshot,
  getFirstSnapshotHandler,
  findFirstExpressionFocusTarget,
  restoreFocusAfterBulkClear,
  planBulkClear,
  getPartialExpressionElement,
  deletePartialExpression,
  getCrossExpressionElements,
  deleteCrossExpressionPartial,
  consumePreRestoreEndpoints,
  type SpinButtonSnapshotEntry,
  getLastSnapshotHandler,
} from './selection-helpers.js';
import type { DateTimeInputRef } from './types.js';

/**
 * Builds an array of input references from the given refs.
 * @internal
 */
function getInputRefs(
  dateInputFromRef: RefObject<DateTimeInputRef>,
  dateInputToRef?: RefObject<DateTimeInputRef>,
): RefObject<DateTimeInputRef>[] {
  return [dateInputFromRef, ...(dateInputToRef ? [dateInputToRef] : [])];
}

/**
 * Hook that provides interaction handlers for selection behavior in date/time pickers.
 * Manages mouse and keyboard interactions including text selection and focus management.
 *
 * @param touchedContainerRef - Reference to the container that should be checked when forwarding focus
 * @param onFocusInput - Callback to focus the appropriate input(s) when needed
 * @param dateInputFromRef - Reference to the "from" DateTimeInput imperative handle
 * @param dateInputToRef - Reference to the optional "to" DateTimeInput imperative handle
 * @returns Object containing onMouseDown and onKeyDown event handlers
 *
 * @internal
 */
export function useSelectionInteractionHandlers(
  touchedContainerRef: RefObject<HTMLElement>,
  onFocusInput: () => void,
  dateInputFromRef: RefObject<DateTimeInputRef>,
  dateInputToRef?: RefObject<DateTimeInputRef>,
): {
  onMouseDown: (event: ReactMouseEvent<HTMLDivElement>) => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
} {
  const onMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return;
      }
      // Only compute the anchor spinbutton for clicks in empty space (not on
      // a spinbutton element itself). For direct spinbutton clicks the browser's
      // native anchor is correct and must be preserved for single-element
      // intra-spinbutton drag to work normally.
      const root: HTMLElement = event.currentTarget;
      const triggerElement = event.target;
      const clickedOnSpinButton =
        triggerElement instanceof HTMLElement &&
        triggerElement.hasAttribute('contenteditable');
      let initialAnchorSpinButton: HTMLElement | null = null;
      if (!clickedOnSpinButton) {
        const spinButtons = Array.from(
          root.querySelectorAll<HTMLElement>('[contenteditable]'),
        );
        // Find the rightmost spinbutton whose right edge is at or before the
        // click x — that is the spinbutton the drag started on (or the last
        // one to its left when clicking in empty space).
        let bestRight = -Infinity;
        for (const sb of spinButtons) {
          const rect = sb.getBoundingClientRect();
          if (rect.right <= event.clientX && rect.right > bestRight) {
            initialAnchorSpinButton = sb;
            bestRight = rect.right;
          }
        }
        // Clicked before all spinbuttons → fall back to the first one.
        if (initialAnchorSpinButton === null && spinButtons.length > 0) {
          initialAnchorSpinButton = spinButtons[0] ?? null;
        }
      }
      window.getSelection()?.removeAllRanges();
      if (root) {
        removeContentEditable(
          root,
          document,
          'mouseup',
          (mouseUpEvent: Event, hasMultiElementSelection: boolean) => {
            if (
              !hasMultiElementSelection &&
              mouseUpEvent.target instanceof HTMLElement &&
              mouseUpEvent.target === triggerElement
            ) {
              // If the click landed on a contenteditable element (spin button
              // or expression input), focus it directly. Otherwise the click
              // was on the container or a non-interactive child, so forward
              // focus to the first input.
              if (mouseUpEvent.target.hasAttribute('contenteditable')) {
                mouseUpEvent.target.focus();
              } else {
                onFocusInput();
              }
            }
          },
          initialAnchorSpinButton,
        );
      }
    },
    [onFocusInput],
  );

  // Snapshot of the spin-button values wiped by the last bulk-clear.
  const lastClearedSnapshotRef = useRef<SpinButtonSnapshotEntry[]>([]);
  // Snapshot of expression values wiped by the last bulk-clear (for undo).
  const lastClearedExpressionsRef = useRef<
    { inputRef: RefObject<DateTimeInputRef>; previousText: string }[]
  >([]);

  const onKeyDown = useCallback(
    async (event: ReactKeyboardEvent<HTMLDivElement>) => {
      // Accept either Meta (Mac Cmd) or Ctrl as the modifier key so that the
      // handler works consistently across platforms and in automated test
      // environments (e.g. Playwright's Chromium) where the userAgent may not
      // reliably indicate the OS.
      const hasModifierKey = event.metaKey === true || event.ctrlKey === true;
      const pickerRoot = event.currentTarget;

      // If keydown event captures modifier keys by itself or a copy, do nothing
      if (
        event.key === 'Shift' ||
        event.key === 'Control' ||
        event.key === 'Meta' ||
        event.key === 'Alt' ||
        event.key === 'Command'
      ) {
        event.preventDefault();
        return;
      }

      // Applies the plan returned by planBulkClear: saves undo snapshots,
      // clears all affected spin buttons and expression inputs, and collapses
      // the selection.
      function applyBulkClear(
        plan: ReturnType<typeof planBulkClear>,
        selection: Selection,
      ) {
        const {
          snapshot,
          affectedExpressionRefs,
          expressionFallbacks,
          clearedExpressions,
        } = plan;
        lastClearedSnapshotRef.current = snapshot;
        lastClearedExpressionsRef.current = clearedExpressions;
        for (const { inputRef, key } of snapshot) {
          inputRef.current?.spinButtonHandlers.get(key)?.clearValue(true);
        }
        for (const [inputRef, text] of expressionFallbacks) {
          inputRef.current?.setExpressionValue(text);
        }
        for (const ref of affectedExpressionRefs) {
          ref.current?.setExpressionValue('');
        }
        selection.removeAllRanges();
      }

      // Computes the bulk-clear plan, applies it, and returns focus targets.
      // Used by delete, cut, and type-to-replace.
      function bulkClear(
        selection: Selection,
        direction: 'forward' | 'backward',
      ) {
        const inputRefs = getInputRefs(dateInputFromRef, dateInputToRef);

        const partialExpressionElement = getPartialExpressionElement(
          selection,
          inputRefs,
        );
        if (partialExpressionElement) {
          onBeforeExpressionDelete([partialExpressionElement], inputRefs);
          deletePartialExpression(selection, partialExpressionElement);
          return;
        }

        const crossExpressionElements = getCrossExpressionElements(
          selection,
          inputRefs,
        );
        if (crossExpressionElements) {
          const isSameExpressionSelection =
            crossExpressionElements[0] === crossExpressionElements[1];
          const expressionElementsForUndo = isSameExpressionSelection
            ? [crossExpressionElements[0]]
            : crossExpressionElements;
          onBeforeExpressionDelete(expressionElementsForUndo, inputRefs);

          const boundaryExpressionRef = isSameExpressionSelection
            ? inputRefs.find(
                (inputRef) =>
                  inputRef.current?.expressionInputRef ===
                  crossExpressionElements[0],
              )
            : undefined;

          const plan = planBulkClear(
            selection,
            inputRefs,
            consumePreRestoreEndpoints(pickerRoot),
          );
          lastClearedSnapshotRef.current = plan.snapshot;
          lastClearedExpressionsRef.current = plan.clearedExpressions;

          const firstSpinButtonHandler = getFirstSnapshotHandler(plan.snapshot);
          const expressionFocusTarget = findFirstExpressionFocusTarget(
            inputRefs,
            plan.affectedExpressionRefs,
            plan.expressionFallbacks,
          );

          deleteCrossExpressionPartial(selection, crossExpressionElements);

          for (const { inputRef, key } of plan.snapshot) {
            inputRef.current?.spinButtonHandlers.get(key)?.clearValue(true);
          }
          for (const [inputRef, text] of plan.expressionFallbacks) {
            if (inputRef !== boundaryExpressionRef) {
              inputRef.current?.setExpressionValue(text);
            }
          }

          restoreFocusAfterBulkClear(
            firstSpinButtonHandler,
            expressionFocusTarget,
          );

          return;
        }

        const plan = planBulkClear(
          selection,
          inputRefs,
          consumePreRestoreEndpoints(pickerRoot),
        );
        const nextFocusedElementHandler =
          direction === 'backward'
            ? getFirstSnapshotHandler(plan.snapshot)
            : getLastSnapshotHandler(plan.snapshot);
        const expressionFocusTarget = findFirstExpressionFocusTarget(
          inputRefs,
          plan.affectedExpressionRefs,
          plan.expressionFallbacks,
        );
        applyBulkClear(plan, selection);
        restoreFocusAfterBulkClear(
          nextFocusedElementHandler,
          expressionFocusTarget,
        );
      }

      // Saves the current text of each expression element as an undo snapshot
      // before a partial or cross-expression deletion that bypasses applyBulkClear.
      function onBeforeExpressionDelete(
        expressionElements: HTMLSpanElement[],
        inputRefs: RefObject<DateTimeInputRef>[],
      ) {
        lastClearedSnapshotRef.current = [];

        const expressionSnapshots: {
          inputRef: RefObject<DateTimeInputRef>;
          previousText: string;
        }[] = [];

        for (const exprEl of expressionElements) {
          let matchingRef: RefObject<DateTimeInputRef> | undefined;
          for (const inputRef of inputRefs) {
            if (inputRef.current?.expressionInputRef === exprEl) {
              matchingRef = inputRef;
              break;
            }
          }

          if (matchingRef) {
            expressionSnapshots.push({
              inputRef: matchingRef,
              previousText:
                matchingRef.current?.inputRef?.value ??
                exprEl.textContent ??
                '',
            });
          }
        }

        lastClearedExpressionsRef.current = expressionSnapshots;
      }

      // Handle select all with Ctrl + A
      if (hasModifierKey && event.key === 'a') {
        event.preventDefault();
        if (event.currentTarget instanceof HTMLElement) {
          // No need to remove contentEditable here: extendSelectionToAll explicitly focuses the first spin button
          // and then sets the cross-element range with addRange, keeping the focus on the already focused spin button.
          extendSelectionToAll();
        }
        return;
      }

      // Check if the selection should be expanded with Shift
      if (
        event.shiftKey === true &&
        (event.key === 'ArrowLeft' ||
          event.key === 'ArrowRight' ||
          event.key === 'Home' ||
          event.key === 'End')
      ) {
        event.preventDefault();
        if (event.currentTarget instanceof HTMLElement) {
          // extendSelection uses setBaseAndExtent internally, which can cross
          // contenteditable boundaries without needing to strip the attribute.
          // No removeContentEditable / focus manipulation is needed here.
          extendSelection(event.key);
        }
        return;
      }

      // Handle undo (Ctrl/Cmd+Z): restore the last bulk-cleared values.
      if (hasModifierKey && event.key === 'z') {
        const snapshot = lastClearedSnapshotRef.current;
        const expressionEntries = lastClearedExpressionsRef.current;
        if (snapshot.length === 0 && expressionEntries.length === 0) {
          return;
        }
        event.preventDefault();
        restoreSpinButtonSnapshot(snapshot);
        lastClearedSnapshotRef.current = [];

        for (const { inputRef, previousText } of expressionEntries) {
          inputRef.current?.setExpressionValue(previousText);
          inputRef.current?.validate();
        }
        lastClearedExpressionsRef.current = [];

        if (snapshot.length > 0) {
          // Focus the first restored spin button (synchronous).
          getFirstSnapshotHandler(snapshot)?.element.current?.focus();
        } else {
          // Only expressions were restored — focus the first one after its rAF.
          requestAnimationFrame(() => {
            expressionEntries[0]?.inputRef.current?.expressionInputRef?.focus();
          });
        }
        return;
      }

      const isCut = hasModifierKey && event.key === 'x';
      const isDelete = event.key === 'Backspace' || event.key === 'Delete';
      const direction =
        isCut || event.key === 'Backspace' ? 'backward' : 'forward';

      if (isCut || isDelete) {
        const selection = window.getSelection();
        if (selection && !selection.isCollapsed) {
          event.preventDefault();

          const clipboardText = buildTimeframeClipboardText();
          // Apply the clear and update focus synchronously BEFORE awaiting the
          // clipboard write. This ensures the undo snapshot is saved inside the
          // current synchronous execution context so that a Ctrl+Z fired
          // immediately afterwards (e.g. in tests wrapped with act()) can always
          // find a non-empty lastClearedSnapshotRef.
          bulkClear(selection, direction);

          if (isCut) {
            if (clipboardText !== null) {
              try {
                await navigator.clipboard.writeText(clipboardText);
              } catch {
                // For now, we silently ignore clipboard write errors.
              }
            }
          }
          return;
        }
      }

      // When there is a cross-element selection and the user types a printable
      // character, bulk-clear the selection and forward the key to the anchor
      // spin button so it can process the character normally.
      // Intra-element selections (drag within a single spin button) are left
      // alone — the spin button's own beforeinput handler deals with those.
      if (
        !hasModifierKey &&
        !event.shiftKey &&
        event.key.length === 1 &&
        (isExpressionTriggerKey(event) || /^\d$/.test(event.key))
      ) {
        const selection = window.getSelection();
        if (
          selection &&
          !selection.isCollapsed &&
          selection.anchorNode !== selection.focusNode
        ) {
          event.preventDefault();
          const inputRefs = getInputRefs(dateInputFromRef, dateInputToRef);
          const plan = planBulkClear(
            selection,
            inputRefs,
            consumePreRestoreEndpoints(pickerRoot),
          );

          // When ALL spin buttons of an affected input are cleared (0 survivors),
          // the input should switch to expression mode — use the full applyBulkClear
          // path (same as Delete). Detected by: expression fallbacks will fire AND
          // the selection starts at the first spin button of its input.
          // When the first spin button is the survivor (selection starts later),
          // skip expression fallbacks so the input stays in ISO mode and the typed
          // character lands in the first selected spin button.
          const firstEntry = plan.snapshot[0];
          const firstKey = firstEntry
            ? [
                ...(firstEntry.inputRef.current?.spinButtonHandlers.keys() ??
                  []),
              ][0]
            : undefined;
          const expressionModeWillSwitch =
            plan.expressionFallbacks.size > 0 && firstEntry?.key === firstKey;

          if (expressionModeWillSwitch) {
            const firstSpinButtonHandler = getFirstSnapshotHandler(
              plan.snapshot,
            );
            const expressionFocusTarget = findFirstExpressionFocusTarget(
              inputRefs,
              plan.affectedExpressionRefs,
              plan.expressionFallbacks,
            );
            applyBulkClear(plan, selection);
            // Pre-fill the expression input with the typed character. Both this
            // call and the setExpressionValue('') inside applyBulkClear are
            // batched by React 18, so the expression input renders with the
            // character before the rAF in makeFocusAfterBulkClear fires.
            expressionFocusTarget?.current?.setExpressionValue(event.key);
            restoreFocusAfterBulkClear(
              firstSpinButtonHandler,
              expressionFocusTarget,
            );
            return;
          }

          // Save undo snapshot and clear only the spin buttons — skip expression
          // fallbacks so the input stays in ISO mode and focus can land on the
          // first spin button to receive the typed character.
          lastClearedSnapshotRef.current = plan.snapshot;
          lastClearedExpressionsRef.current = plan.clearedExpressions;
          for (const { inputRef, key } of plan.snapshot) {
            inputRef.current?.spinButtonHandlers.get(key)?.clearValue(true);
          }
          selection.removeAllRanges();

          // Get the handler for the first cleared spin button directly from
          // the snapshot. We cannot use getFirstSnapshotHandler here because
          // it intentionally returns undefined for the first/second keys
          // (designed for post-delete focus), which would swallow the character.
          const handler = firstEntry
            ? firstEntry.inputRef.current?.spinButtonHandlers.get(
                firstEntry.key,
              )
            : undefined;
          if (handler) {
            handler.element.current?.focus();
            if (handler.type === 'number') {
              handler.applyText(event.key, true);
              const newLength =
                handler.element.current?.textContent?.length ?? 0;
              handler.setSelection({ start: newLength, end: newLength });
            } else {
              handler.element.current?.dispatchEvent(
                new KeyboardEvent('keydown', {
                  key: event.key,
                  bubbles: true,
                  cancelable: true,
                }),
              );
            }
          }
          return;
        }
      }

      // When the container itself has focus (post drag-select, no spin button focused),
      // intercept ArrowUp / ArrowDown and forward them to the anchor spin button.
      // This avoids the Firefox visual bug caused by focusing a spin button during
      // drag-restore — no focus() is called there, so the container holds focus.
      if (
        !event.shiftKey &&
        !hasModifierKey &&
        (event.key === 'ArrowUp' || event.key === 'ArrowDown') &&
        event.target === touchedContainerRef.current
      ) {
        const anchorSpinButton = getAnchorSpinButton();

        if (anchorSpinButton !== null) {
          event.preventDefault();
          // Focus the spin button. onFocus sees selectionSpansMultipleNodes = true
          // (the cross-element range is still intact) and skips selectAll().
          anchorSpinButton.focus();
          // Dispatch a synthetic keydown to trigger useValueNavigation on the spin button.
          anchorSpinButton.dispatchEvent(
            new KeyboardEvent('keydown', {
              key: event.key,
              bubbles: true,
              cancelable: true,
            }),
          );
          return;
        }
      }

      // Forward the focus when a key is pressed in this element.
      // If the container has focus with an active selection (post Cmd+A or drag),
      // forward to the anchor spin button so the key lands in the right field.
      // Otherwise fall back to onFocusInput() which focuses the first input.
      // Skip for modifier+key shortcuts (e.g. Cmd+C): focusing a spin button
      // collapses Firefox's cross-element selection before the copy event fires.
      if (event.target === touchedContainerRef.current && !hasModifierKey) {
        const anchorSpinButton = getAnchorSpinButton();

        if (anchorSpinButton !== null) {
          anchorSpinButton.focus();
        } else {
          onFocusInput();
        }
      }
    },
    [onFocusInput, touchedContainerRef, dateInputFromRef, dateInputToRef],
  );

  return { onMouseDown, onKeyDown };
}

function getAnchorSpinButton(): HTMLElement | null {
  const anchorNode = window.getSelection()?.anchorNode ?? null;
  if (anchorNode === null) {
    return null;
  }

  return anchorNode.nodeType === Node.TEXT_NODE
    ? (anchorNode as Text).parentElement?.closest<HTMLElement>(
        '[contenteditable]',
      ) ?? null
    : (anchorNode as HTMLElement | null)?.closest?.('[contenteditable]') ??
        null;
}
